import { Modal } from '@mui/material';
import { X } from 'react-feather';
import React, { useEffect, useMemo, useState } from 'react';
import { AppButton } from '../../components/general/AppButton/AppButton';
import { AppInputField } from '../../components/general/AppInputField/AppInputField';
import { COLORS } from '../../utils/colors';
import { AppDropDown2 } from '../../components/general/AppDropDown2/AppDropDown2';
import { getAllStates } from '@dill/dill-shared';

const INITIAL_STATE = {
  address: '',
  city: '',
  state: '',
  zipCode: '',
};

export const AppEditBuyerAddressModal = ({
  open,
  handleClose,
  onSubmit,
  address,
}: {
  open: boolean;
  handleClose: () => void;
  onSubmit: (data: { address: string; city: string; state: string; zipCode: string }) => void;
  address: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
}) => {
  const [buyerAddress, setBuyerAddress] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState(INITIAL_STATE);
  const [showErrorAbove, setShowErrorAbove] = useState(false);

  useEffect(() => {
    if (address) {
      setBuyerAddress({
        address: address.address,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
      });
    }
  }, [address]);

  const allStates = useMemo(() => {
    return getAllStates();
  }, []);

  const onTextChange = (key: string, value: string) => {
    setBuyerAddress((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const handleCloseModal = () => {
    setBuyerAddress(INITIAL_STATE);
    setErrors(INITIAL_STATE);
    handleClose();
  };
  const handleSubmit = () => {
    setShowErrorAbove(false);
    const newErrors = {} as { address: string; city: string; state: string; zipCode: string };
    if (buyerAddress.address.trim() === '') {
      newErrors.address = 'Please enter a job address';
    }
    if (buyerAddress.city.trim() === '') {
      newErrors.city = 'Please enter a job city';
    }
    if (buyerAddress.state.trim() === '') {
      newErrors.state = 'Please enter a job state';
    }
    if (buyerAddress.zipCode.trim() === '') {
      newErrors.zipCode = 'Please enter a job zip code';
    }
    setErrors(newErrors);
    if (Object.values(newErrors).some((error) => error)) {
      setShowErrorAbove(true);
      return;
    }
    onSubmit(buyerAddress);
    handleCloseModal();
  };

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <div className="flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg  flex-col w-[512px]">
        <div className="flex flex-row items-center justify-between border-b-[1px] border-solid border-[#cbd5e1] px-4 py-2">
          <h3 className="font-medium text-lg">{'Edit Address'}</h3>
          <AppButton
            type="FLAT"
            buttonStyles={{ padding: '0px', margin: '5px 2px' }}
            icon={<X color={COLORS.GREY_300} size={30} />}
            onClick={handleCloseModal}
          />
        </div>
        <div className="flex flex-col w-full p-4">
          <div className="flex">
            <div className="w-full mb-3">
              <AppInputField
                id="address"
                label="Address"
                isRequired={true}
                value={buyerAddress.address}
                errorText={errors.address ? errors.address : ''}
                onTextChange={(text) => {
                  onTextChange('address', text);
                }}
              />
            </div>
          </div>
          <div className="flex flex-row justify-around gap-2">
            <div className="w-full">
              <AppInputField
                id="city"
                label="City"
                isRequired={true}
                value={buyerAddress.city}
                errorText={errors.city ? errors.city : ''}
                onTextChange={(text) => {
                  onTextChange('city', text);
                }}
              />
            </div>
            <div className="w-full">
              <AppDropDown2
                isRequired
                label="State"
                value={buyerAddress.state || null}
                errorText={errors.state ? errors.state : ''}
                items={[
                  ...Object.keys(allStates).map((state) => {
                    const key = state as keyof typeof allStates;
                    const stateKey = allStates[key];
                    return {
                      label: `${stateKey} - ${state}`,
                      value: stateKey,
                    };
                  }),
                ]}
                onSelectChange={(item) => {
                  if (item && item?.value !== null) {
                    const foundState = Object.values(getAllStates()).find(
                      (state) => state === item?.value
                    );
                    if (foundState) {
                      onTextChange('state', item.value);
                    }
                  }
                }}
              />
            </div>
            <div className="w-full">
              <AppInputField
                id="zip"
                label="Zip Code"
                isRequired={true}
                value={buyerAddress.zipCode}
                errorText={errors.zipCode ? errors.zipCode : ''}
                onTextChange={(text) => {
                  onTextChange('zipCode', text);
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row-reverse px-4 py-2 gap-2 border-t-[1px] border-solid border-[#cbd5e1] w-full mt-2">
          <AppButton text={'Submit'} onClick={handleSubmit} />
          <AppButton text={'Close'} type="TERTIARY" onClick={handleCloseModal} />
        </div>
      </div>
    </Modal>
  );
};
